import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Helpers
import { kelvin_to_rgb, percent_from_value_range, capFirstChar } from 'src/helpers/utilities';
//Types
import type { ActionJob, ActionJobParams } from 'src/types/requests/schedules/schedules_g2';
import type { LabelProps } from '@Shelly/shelly-design-system/index';
import type { ActionRule, I18nCtx, LightsActionParams } from '../rules/actions_rules';

export interface LightsActionRule extends ActionRule {
	when: (params: LightsActionParams, toLabel?: boolean) => boolean;
	toString: (params: LightsActionParams, ctx: I18nCtx) => string | null;
	toLabel: (params: LightsActionParams, ctx: I18nCtx) => LabelProps | null;
}

const LIGHTS_ACTIONS_RULES: LightsActionRule[] = [
	{
		key: 'on',
		when: (p) => p.on !== undefined,
		toString: (p, ctx) =>
			`${ctx.translate('label_light_state', 'Light State')}: ${
				p.on ? ctx.translate('on_action', 'on') : ctx.translate('off_action', 'off')
			}`,
		toLabel: (p) => {
			const iconColor = getIconColor(p.rgb) || getIconCCT(p.ct);
			const isOn = p.on === true;
			return {
				icon: isOn
					? 'fa fa-lightbulb-on'
					: iconColor
						? 'fa-duotone fa-light fa-lightbulb'
						: 'fa fa-lightbulb',
				...(iconColor &&
					isOn && {
						iconColor,
					}),
				...(iconColor &&
					!isOn && {
						iconStyle: `--fa-primary-color: ${iconColor}; --fa-secondary-color: ${iconColor}; --fa-secondary-opacity: 1`,
					}),
			};
		},
	},
	{
		key: 'rgb',
		when: (p, toLabel) => {
			return (!toLabel || p.on === undefined) && getIconColor(p.rgb) !== undefined;
		},
		toString: (p, ctx) => {
			const iconColor = getIconColor(p.rgb);
			return `${capFirstChar(ctx.translate('label_color', 'Color'))}: 
				<i class="fas fa-lightbulb-on" style="color:${iconColor};margin:0 5px;display:inline-block"></i>`;
		},
		toLabel: (p) => {
			const iconColor = getIconColor(p.rgb);
			return {
				icon: 'fa fa-lightbulb-on',
				iconColor,
			};
		},
	},
	{
		key: 'ct',
		when: (p) => Number.isFinite(p.ct),
		toString: (p, ctx) =>
			`${ctx.translate('label_control_temperature', 'Color Temperature')}: ${p.ct} K`,
		toLabel: (p) => ({ value: `${p.ct} K` }),
	},
	{
		key: 'brightness',
		when: (p) => Number.isFinite(p.brightness),
		toString: (p, ctx) => `${ctx.translate('label_brightness', 'Brightness')}: ${p.brightness}%`,
		toLabel: (p) => ({ value: `${p.brightness}%` }),
	},
	{
		key: 'white',
		when: (p) => !Number.isFinite(p.brightness) && Number.isFinite(p.white),
		toString: (p, ctx) => {
			const val = percent_from_value_range(p.white, [0, 255]);
			return `${ctx.translate('label_white_brightness', 'White brightness')}: ${Math.round(val)}%`;
		},
		toLabel: (p) => {
			const val = percent_from_value_range(p.white, [0, 255]);
			return { value: `${Math.round(val)}%`, icon: 'fa fa-brightness' };
		},
	},
	{
		key: 'transition_duration',
		when: (p) => Number.isFinite(p.transition_duration),
		toString: (p, ctx) =>
			`${ctx.translate('label_transition', 'Transition')}: ${p.transition_duration}s`,
		toLabel: () => null,
	},
	{
		key: 'offset',
		when: (p) => Number.isFinite(p.offset),
		toString: (p, ctx) => `${ctx.translate('label_offset', 'Offset')}: ${p.offset}%`,
		toLabel: (p) => ({ value: `${p.offset}%`, icon: 'fas fa-palette' }),
	},
	{
		key: 'offset_white',
		when: (p) => Number.isFinite(p.offset_white),
		toString: (p, ctx) =>
			`${ctx.translate('label_offset_white', 'Offset White')}: ${p.offset_white}%`,
		toLabel: (p) => ({ value: `${p.offset_white}%`, icon: 'fas fa-sliders' }),
	},
];

export const stringifyLightsAction = (action: ActionJob): string => {
	const $format = get(format);
	const params: ActionJobParams = action.params;
	const ctx: I18nCtx = { translate: (k, d) => $format(k, { default: d }) };
	return LIGHTS_ACTIONS_RULES.filter((r) => r.when(params, false))
		.map((r) => r.toString(params, ctx))
		.join(', ');
};

export const buildLightLabels = (job: ActionJob, labelList: LabelProps[]): LabelProps[] => {
	const $format = get(format);
	const params: ActionJobParams = job.params;
	const ctx: I18nCtx = { translate: (k, d) => $format(k, { default: d }) };
	LIGHTS_ACTIONS_RULES.filter((r) => r.when(params, true)).forEach((r) => {
		const lp = r.toLabel(params, ctx);
		if (lp) labelList.push(lp);
	});
	return labelList;
};

export const isValidRGB = (c: number[]): boolean => {
	return (
		Array.isArray(c) && c.length === 3 && c.every((n) => Number.isFinite(n) && n >= 0 && n <= 255)
	);
};

export const getIconColor = (color: number[]): string | undefined => {
	if (isValidRGB(color)) {
		return `rgb(${color[0]},${color[1]},${color[2]})`;
	}
	return undefined;
};

export const getIconCCT = (cct: number): string | undefined => {
	return getIconColor(kelvin_to_rgb(cct));
};
